import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import { Stack, Theme, TypographyProps, useTheme } from "@mui/material";

import { FilledColoredCircle } from "@/Components/v2/atoms/ColoredCircle";
import { Tooltip } from "@/Components/v2/atoms/ToolTip";
import { Typography } from "@/Components/v2/atoms/Typography";
import { colors } from "@/Components/v2/utils/brand-colors";

export interface LegendItemData {
  id: string;
  color: string;
  title: string;
  subTitle?: string;
  description?: string;
  isActive: boolean;
  isDisabled?: boolean;
}

interface LegendItemProps {
  item: LegendItemData;
  handleClick: () => void;
  withBorder?: boolean;
  tooltip?: string;
}

interface ItemTextProps {
  isActive: boolean;
  children: React.ReactNode;
}

const ItemText: React.FC<ItemTextProps & TypographyProps> = ({ isActive, children, ...props }) => {
  return (
    <Typography
      variant="body2"
      color={isActive ? "inherit" : colors.dropdownBorder}
      style={{ opacity: isActive ? 1 : 0.5 }}
      {...props}
    >
      {children}
    </Typography>
  );
};

const borderStyles = (theme: Theme) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 20,
  py: 0.5,
  px: 1,
});

export const LegendItem: React.FC<LegendItemProps> = ({
  item,
  handleClick,
  withBorder = false,
  tooltip,
}) => {
  const theme = useTheme();
  return (
    <Stack
      direction="row"
      sx={{ cursor: item.isDisabled ? "default" : "pointer" }}
      spacing={1}
      onClick={item.isDisabled ? undefined : handleClick}
      {...(withBorder && borderStyles(theme))}
    >
      <FilledColoredCircle
        sx={{ marginTop: "4px !important" }}
        color={
          item.isDisabled
            ? colors.dropdownBorder
            : item.isActive
              ? item.color
              : colors.dropdownBorder
        }
      />
      <Stack>
        <ItemText isActive={!item.isDisabled && item.isActive} variant="body2" fontWeight={600}>
          {item.title}
        </ItemText>
        <ItemText isActive={!item.isDisabled && item.isActive} fontSize={12}>
          {item.subTitle}
        </ItemText>
        <ItemText isActive={!item.isDisabled && item.isActive} fontSize={12}>
          {item.description}
        </ItemText>
      </Stack>
      {tooltip && (
        <Tooltip
          title={
            <Typography
              fontSize={14}
              variant="body1"
              padding={1}
              sx={{ color: theme.palette.common.white }}
            >
              {tooltip}
            </Typography>
          }
          placement="top"
        >
          <HelpOutlineOutlinedIcon
            fontSize="small"
            sx={{ color: theme.palette.brandedTypography.textColor }}
          />
        </Tooltip>
      )}
    </Stack>
  );
};
